import React, { FC, useState } from "react";
import { LuLock } from "react-icons/lu";
import { useNavigate } from "react-router-dom";

import { useGetClientPortalQuery } from "../../slices/ClientPortalSlice";
import { portalSummary } from "./clientPortal.utils";
import { InviteToPortalModal } from "./InviteToPortalModal";

const LOOK = {
  active: { dot: "bg-[#22A06B]", label: "Active" },
  invited: { dot: "bg-[#D99A00]", label: "Invite sent" },
  none: { dot: "bg-[#B8BECB]", label: "Not invited" },
} as const;

/**
 * Small "Client portal" box in the client's information card (owner Loom aa30b45b,
 * 2026-10-28): green "Active" + Manage (opens Client portal > People), or
 * "Not invited" + Invite (opens the invite window). "Invite sent" while only
 * invitations are out (Manage, like Active).
 */
export const PortalInfoRow: FC<{ customerId?: string | null }> = ({
  customerId,
}) => {
  const navigate = useNavigate();
  const [inviteOpen, setInviteOpen] = useState(false);
  const { data } = useGetClientPortalQuery(
    { customerId: customerId || "" },
    { skip: !customerId },
  );
  if (!customerId || !data) return null;
  const state = portalSummary(
    (data.people ?? []).filter((p) => p.status !== "revoked"),
  );
  const look = LOOK[state];
  const invite = state === "none";
  return (
    <>
      <div
        data-testid={"portal-info-row"}
        className={
          "mb-1 mt-3 flex items-center gap-2.5 rounded-[10px] border border-[#EDEFF3] bg-[#FAFAFC] px-3 py-2.5"
        }>
        <LuLock size={14} className={"shrink-0 text-[#7C5CFC]"} />
        <div className={"min-w-0 flex-1"}>
          <div className={"text-[11.5px] font-medium text-[#6B7185]"}>
            Client portal
          </div>
          <div
            className={
              "flex items-center gap-1.5 text-[13px] font-semibold text-[#1F2433]"
            }>
            <span className={`h-1.5 w-1.5 rounded-full ${look.dot}`} />
            {look.label}
          </div>
        </div>
        <button
          type={"button"}
          disabled={invite && !!data.archived}
          title={
            invite && data.archived
              ? "This client is archived. Unarchive it to invite people."
              : undefined
          }
          onClick={() =>
            invite
              ? setInviteOpen(true)
              : navigate("../portal?view=people", { relative: "path" })
          }
          className={
            "shrink-0 text-[12.5px] font-bold text-[#6B4EFF] hover:underline disabled:cursor-not-allowed disabled:opacity-50"
          }>
          {invite ? "Invite" : "Manage"}
        </button>
      </div>
      {inviteOpen && !data.archived && (
        <InviteToPortalModal
          overview={data}
          onClose={() => setInviteOpen(false)}
        />
      )}
    </>
  );
};
